import React, { useEffect, useState } from "react"
import SSESubscriberService from "../../../services/SSESubscriberService"
import { logCheckoutConfirmed } from "../../../context/analytics-handlers"
import { useAPI } from "../../../services/api"
import { CheckoutOrder } from "../../../models/order"
import { getErrorMessage } from "../../../util/error-handler"
import { useTranslation, useI18next } from "gatsby-plugin-react-i18next"
import { useGlobalModals } from "../../../context/GlobalModalsContext"
import { translationKeys } from "../../../util/translationKeys"
import { usePaymentService } from "../../../hooks/use-payment-service"
import { trackGTMPurchase } from "../../../services/gtm-datalayer-handlers"
import { captureException } from "../../../services/exception-logger"

type Props = {
  src: string
  providerName: string
  startTimeout: (
    providerName: string,
    preCancelAction: () => void | boolean
  ) => void
  sseSubscriber: React.MutableRefObject<SSESubscriberService | undefined>
  showPaymentSucceededScreen: () => void
  showPaymentFailedScreen: (errorMessage?: string) => void
  order: CheckoutOrder
  timeoutId: React.MutableRefObject<NodeJS.Timeout | undefined>
}

export const WalletPaymentIFrame = ({
  src,
  providerName,
  startTimeout,
  sseSubscriber,
  showPaymentSucceededScreen,
  order,
  timeoutId,
  showPaymentFailedScreen,
}: Props) => {
  const [_userActionsDone, _setUserActionsDone] = useState(false)
  const { handleCancelPayment } = usePaymentService(order.order.id)
  const api = useAPI()
  const { t } = useTranslation()
  const { language } = useI18next()
  const { setInfoModalConfig } = useGlobalModals()
  const userActionsDone = React.useRef(_userActionsDone)
  // Using states in event listeners will always read initial state, so we have to update a ref in order for the event listener to access the latest state
  const setUserActionsDone = (val: boolean) => {
    userActionsDone.current = val
    _setUserActionsDone(val)
  }

  // we have this explicit check because mobile Safari will "freeze" the app while it is in background
  // meaning that SSE events won't be triggered while user has MobilePay app opened
  const checkIfOrderIsPaid = async () => {
    if (document.visibilityState !== "visible") {
      return
    }
    if (!order.order.id) {
      captureException(
        new Error("Order ID is undefined in checkIfOrderIsPaid!"),
        {
          type: "business_logic",
          category: "invalid-state",
          context: { errorSource: "WalletPaymentIFrame", providerName },
        }
      )
      return
    }

    let result
    try {
      result = await api.checkout.retrieveOrderById(order.order.id, language)
    } catch (error) {
      const errorMessage = getErrorMessage(error, t)
      setInfoModalConfig({
        title: t(translationKeys.comboPaymentErrorGenericHeader),
        description: errorMessage,
      })
      return
    }

    // TODO extract accepted states list and replace all usages in code
    const acceptedStates = [
      "paid",
      "accepted",
      "processing",
      "manufacturing",
      "ready",
      "in_delivery",
      "delivered",
    ]

    // If the order is in new or checkout state and a timeout is not processing, we start a timeout
    // Otherwise we do nothing
    if (!acceptedStates.includes(result?.order.state || "")) {
      // If the timeout is already started or user actions are not done (entering phone number etc.), we do nothing
      if (timeoutId.current || !userActionsDone.current) {
        return
      }
      startTimeout(providerName, () => {
        sseSubscriber?.current?.close()
      })
      return
    }

    if (result) {
      logCheckoutConfirmed(result.order)
      trackGTMPurchase(result.order)
    }
    showPaymentSucceededScreen()
  }

  const onMessageFromIFrame = async (event: MessageEvent<any>) => {
    const srcUrl = new URL(src)
    const eventOrigin = new URL(event.origin)

    if (srcUrl.hostname !== eventOrigin.hostname) {
      // Must be from iFrame we are expecting
      return
    }

    if (typeof event.data !== "string") {
      // We are only interested in string messages
      return
    }

    const params = new URLSearchParams(event.data)
    const message = params.get("message")

    if (message !== "rejected") {
      // Only thing we are interested in is if the message is "rejected"
      return
    }

    try {
      await handleCancelPayment(order.order.id, showPaymentSucceededScreen)
      const mostRecentOrderResponse = await api.checkout.retrieveOrderById(
        order.order.id,
        language
      )

      const orderIsNewOrInCheckout =
        mostRecentOrderResponse?.order.state === "in_checkout" ||
        mostRecentOrderResponse?.order.state === "new"
      // If the order is in new or checkout state, we show a failed payment screen
      // We also do this in the handleCancelPayment function, but I cannot inject showPaymentFailedScreen into it.
      // Doing it manually here to avoid redirecting to payment succeeded in handleCancelPayment and then directly to payment failed screen.
      // Super edge case that does not happen probably, but worst case is landing on a payment failed screen after user has already paid successfully.
      if (orderIsNewOrInCheckout) {
        showPaymentFailedScreen("Payment was rejected by user.")
      }
    } catch {
      // We tried cancelling the payment, but it failed.
      // We don't want to show an error message here, because the timeout will handle user flow.
    }
  }

  useEffect(() => {
    document.addEventListener("visibilitychange", checkIfOrderIsPaid)
    window.addEventListener("message", onMessageFromIFrame)

    return () => {
      document.removeEventListener("visibilitychange", checkIfOrderIsPaid)
      window.removeEventListener("message", onMessageFromIFrame)
    }
  }, [])

  return (
    <iframe
      src={src}
      className="h-[70vh] md:h-[110vh]"
      onLoad={async (e: any) => {
        try {
          const redirectUrl = e?.target?.contentWindow?.location?.href
          const pageBaseUrl = process.env.GATSBY_PAGE_BASE_URL
          if (redirectUrl.includes(pageBaseUrl)) {
            setUserActionsDone(true)
            startTimeout(providerName, () => {
              sseSubscriber?.current?.close()
            })
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e: unknown) {
          // When we load the iframe, at first its contentWindow.location.href is going to be from mobilepay/vipps/transpayrent/whatever domain.
          // This is when onLoad will trigger and throw a CORS error, telling that we're trying to access a cross-origin frame.
          // That case is not important to us, so we just catch and do nothing.
          // But when payment is executed/cancelled, onLoad will trigger again, this time its href will be our domain, saying where it should redirect back.
          // No error is thrown now, and that's the case we're reacting on with an "if" block above.
        }
      }}
      frameBorder="0"
      scrolling="no"
    />
  )
}
